/**
 * Combobox — Auswahlliste mit Suche, Tastaturbedienung und ohne Abschneiden.
 *
 * Ersetzt die selbstgebauten Dropdowns, die als `position: absolute` im
 * Modal-Rumpf lagen. Die konnten gar nicht anders als abgeschnitten zu werden:
 * ModalShell setzt `overflow-hidden` auf den Rahmen und `overflow-y-auto` auf
 * den Rumpf — beides kappt ein Kind, das darüber hinausragt. Deshalb hängt die
 * Liste hier per Portal am `body` und wird mit `position: fixed` an den Knopf
 * gerechnet. Sie kann damit von keinem Vorfahren beschnitten werden.
 *
 * Die alte Buchstabensuche sprang bei einem einzelnen Tastendruck zum ersten
 * Treffer, ohne ihn auszuwählen — bei 80 Mitgliedern hilft das wenig. Hier
 * wird stattdessen gefiltert: tippen grenzt ein, Pfeiltasten wählen,
 * Enter übernimmt.
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Über der Modal-Ebene. ModalShell vergibt ab 1000 aufwärts in Zehnerschritten
 * (useModalStack), eine Tailwind-Klasse wie `z-50` landet also zuverlässig
 * HINTER jedem Modal. Die Liste gehört immer zum obersten Element, deshalb ein
 * fester Wert oberhalb des gesamten Modal-Bereichs.
 */
const Z_UEBER_MODALEN = 2000;

export interface ComboboxOption {
  id: number | string;
  /** Angezeigter Text, zugleich Grundlage der Suche. */
  label: string;
  /** Optionale zweite Zeile (z. B. Mitgliedsnummer). */
  hint?: string;
  /** Optionales Icon links. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Optionaler kurzer Wert rechts in der Liste (z. B. „94 %"). */
  badge?: string;
  /** Zusätzliche Suchbegriffe, die nicht angezeigt werden. */
  suchbegriffe?: string;
  disabled?: boolean;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value: number | string | null;
  onChange: (id: number | string) => void;
  /** Text im Knopf, solange nichts gewählt ist. */
  placeholder?: string;
  /** Beschriftung für Screenreader und als Titel im mobilen Slide-up. */
  label: string;
  /** Ab wie vielen Einträgen ein Suchfeld erscheint. Standard: 8. */
  sucheAb?: number;
  disabled?: boolean;
  className?: string;
}

/** Umlaute und Zeichensetzung angleichen, damit „Grosse" auch „Große" findet. */
const normalisieren = (text: string) =>
  text
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Treffer bewerten: Wortanfänge zählen mehr als Vorkommen mitten im Wort.
 * „Mü" soll „Müller" vor „Schmüller" bringen.
 */
const bewerten = (option: ComboboxOption, suche: string): number => {
  const heuhaufen = normalisieren(`${option.label} ${option.hint || ''} ${option.suchbegriffe || ''}`);
  const nadel = normalisieren(suche);
  if (!nadel) return 0;
  if (heuhaufen.startsWith(nadel)) return 3;
  if (heuhaufen.split(' ').some((wort) => wort.startsWith(nadel))) return 2;
  if (heuhaufen.includes(nadel)) return 1;
  return -1;
};

export const Combobox: React.FC<ComboboxProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Auswählen …',
  label,
  sucheAb = 8,
  disabled = false,
  className = '',
}) => {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const [aktiv, setAktiv] = useState(0);
  const [position, setPosition] = useState<{ top: number; left: number; width: number; nachOben: boolean } | null>(null);

  const knopfRef = useRef<HTMLButtonElement>(null);
  const sucheRef = useRef<HTMLInputElement>(null);
  const listeRef = useRef<HTMLDivElement>(null);
  const id = useId();

  const gewaehlt = options.find((o) => o.id === value) || null;

  const gefiltert = useMemo(() => {
    if (!suche.trim()) return options;
    return options
      .map((o) => ({ o, punkte: bewerten(o, suche) }))
      .filter((x) => x.punkte >= 0)
      .sort((a, b) => b.punkte - a.punkte)
      .map((x) => x.o);
  }, [options, suche]);

  const mitSuche = options.length >= sucheAb;

  /**
   * Liste am Knopf ausrichten. `fixed` statt `absolute`, damit kein Vorfahr
   * mit overflow sie beschneiden kann; dafür muss bei Scrollen und
   * Größenänderung nachgerechnet werden.
   */
  const positionBerechnen = useCallback(() => {
    const knopf = knopfRef.current;
    if (!knopf) return;
    const r = knopf.getBoundingClientRect();
    const platzUnten = window.innerHeight - r.bottom;
    const gewuenscht = 320;
    // Nach oben klappen, wenn unten zu wenig Platz ist UND oben mehr wäre.
    const nachOben = platzUnten < gewuenscht && r.top > platzUnten;
    setPosition({
      top: nachOben ? Math.max(8, r.top - Math.min(gewuenscht, r.top - 8) - 6) : r.bottom + 6,
      left: r.left,
      width: r.width,
      nachOben,
    });
  }, []);

  useLayoutEffect(() => {
    if (!offen) return;
    positionBerechnen();
    // `true` für die Capture-Phase: so werden auch Scrolls in inneren
    // Containern (Modal-Rumpf) erfasst, nicht nur am Fenster.
    window.addEventListener('scroll', positionBerechnen, true);
    window.addEventListener('resize', positionBerechnen);
    return () => {
      window.removeEventListener('scroll', positionBerechnen, true);
      window.removeEventListener('resize', positionBerechnen);
    };
  }, [offen, positionBerechnen]);

  useEffect(() => {
    if (!offen) return;
    setSuche('');
    const start = Math.max(0, options.findIndex((o) => o.id === value));
    setAktiv(start);
    // Auf dem Handy nicht automatisch fokussieren — sonst springt die
    // Bildschirmtastatur auf und verdeckt die halbe Liste.
    if (mitSuche && window.matchMedia('(min-width: 640px)').matches) {
      window.setTimeout(() => sucheRef.current?.focus(), 30);
    }
  }, [offen, options, value, mitSuche]);

  // Klick nach außen schließt. Der Knopf ist ausgenommen, sonst würde sein
  // eigener Klick sofort wieder schließen.
  useEffect(() => {
    if (!offen) return;
    const beiMaus = (e: MouseEvent) => {
      const ziel = e.target as HTMLElement;
      // Ueber ein Datenattribut statt ueber eine Ref: Slide-up und Aufklappmenue
      // sind zwei Elemente, eine gemeinsame Ref zeigte immer nur auf das
      // zuletzt gerenderte. Auf dem Handy haette der Klick auf einen Eintrag
      // dann als "daneben" gegolten und die Liste vor der Auswahl geschlossen.
      if (ziel?.closest?.('[data-combobox-panel]') || knopfRef.current?.contains(ziel)) return;
      setOffen(false);
    };
    document.addEventListener('mousedown', beiMaus);
    return () => document.removeEventListener('mousedown', beiMaus);
  }, [offen]);

  // Aktiven Eintrag im Blick behalten
  useEffect(() => {
    if (!offen) return;
    const el = listeRef.current?.querySelector<HTMLElement>(`[data-index="${aktiv}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [aktiv, offen]);

  const waehlen = (option: ComboboxOption) => {
    if (option.disabled) return;
    onChange(option.id);
    setOffen(false);
    knopfRef.current?.focus();
  };

  const beiTaste = (e: React.KeyboardEvent) => {
    if (!offen) {
      if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        setOffen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setAktiv((i) => Math.min(i + 1, gefiltert.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setAktiv((i) => Math.max(i - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setAktiv(0);
        break;
      case 'End':
        e.preventDefault();
        setAktiv(gefiltert.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (gefiltert[aktiv]) waehlen(gefiltert[aktiv]);
        break;
      case 'Escape':
        e.preventDefault();
        setOffen(false);
        knopfRef.current?.focus();
        break;
      case 'Tab':
        setOffen(false);
        break;
      default:
        break;
    }
  };

  const listenId = `${id}-liste`;

  const inhalt = (
    <>
      {mitSuche && (
        <div className="border-b border-gray-200 p-2 dark:border-gray-700">
          <div className="relative">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              ref={sucheRef}
              type="text"
              value={suche}
              onChange={(e) => {
                setSuche(e.target.value);
                setAktiv(0);
              }}
              onKeyDown={beiTaste}
              placeholder="Tippen zum Filtern …"
              aria-controls={listenId}
              aria-autocomplete="list"
              className="w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-8 pr-7 text-[13px] text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
            {suche && (
              <button
                type="button"
                onClick={() => { setSuche(''); sucheRef.current?.focus(); }}
                aria-label="Suche leeren"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      <div
        ref={listeRef}
        id={listenId}
        role="listbox"
        aria-label={label}
        className="max-h-[min(320px,60vh)] overflow-y-auto overscroll-contain py-1"
      >
        {gefiltert.length === 0 ? (
          <p className="px-3 py-6 text-center text-[13px] text-gray-500 dark:text-gray-400">
            Nichts gefunden für „{suche}"
          </p>
        ) : (
          gefiltert.map((option, i) => {
            const Icon = option.icon;
            const istGewaehlt = option.id === value;
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                id={`${id}-opt-${i}`}
                data-index={i}
                aria-selected={istGewaehlt}
                disabled={option.disabled}
                onMouseEnter={() => setAktiv(i)}
                onClick={() => waehlen(option)}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors disabled:opacity-40 ${
                  i === aktiv ? 'bg-gray-100 dark:bg-gray-700/60' : ''
                } ${istGewaehlt ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}
              >
                {Icon && <Icon className="h-4 w-4 flex-shrink-0 text-gray-400" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium">{option.label}</span>
                  {option.hint && (
                    <span className="block truncate text-[11.5px] text-gray-500 dark:text-gray-400">{option.hint}</span>
                  )}
                </span>
                {option.badge && (
                  <span className="flex-shrink-0 text-[11.5px] font-semibold tabular-nums text-gray-500 dark:text-gray-400">
                    {option.badge}
                  </span>
                )}
                {istGewaehlt && <CheckIcon className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />}
              </button>
            );
          })
        )}
      </div>
    </>
  );

  return (
    <>
      <button
        ref={knopfRef}
        type="button"
        role="combobox"
        aria-expanded={offen}
        aria-controls={listenId}
        aria-haspopup="listbox"
        aria-label={label}
        aria-activedescendant={offen && gefiltert[aktiv] ? `${id}-opt-${aktiv}` : undefined}
        disabled={disabled}
        onClick={() => setOffen((o) => !o)}
        onKeyDown={beiTaste}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-[13px] transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 ${className}`}
      >
        <span className={`min-w-0 flex-1 truncate ${gewaehlt ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
          {gewaehlt ? gewaehlt.label : placeholder}
        </span>
        <ChevronUpDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
      </button>

      {offen && createPortal(
        <>
          {/* Handy: Slide-up über die volle Breite — ein am Knopf hängendes
              Menü ist auf schmalen Geräten regelmäßig zu groß für den Platz. */}
          <div
            style={{ zIndex: Z_UEBER_MODALEN }}
            className="fixed inset-0 flex items-end bg-black/50 sm:hidden"
            onMouseDown={(e) => { if (e.target === e.currentTarget) setOffen(false); }}
          >
            <div
              data-combobox-panel=""
              className="max-h-[80vh] w-full animate-slide-up overflow-hidden rounded-t-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]"
            >
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5 dark:border-gray-700">
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {label}
                </h3>
                <button
                  type="button"
                  onClick={() => setOffen(false)}
                  aria-label="Schließen"
                  className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              {inhalt}
            </div>
          </div>

          {/* Ab sm: am Knopf ausgerichtet, per fixed unbeschneidbar */}
          {position && (
            <div
              data-combobox-panel=""
              style={{ top: position.top, left: position.left, width: Math.max(position.width, 240), zIndex: Z_UEBER_MODALEN }}
              className="fixed hidden overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg sm:block dark:border-gray-700 dark:bg-[#1c2432]"
            >
              {inhalt}
            </div>
          )}
        </>,
        document.body
      )}
    </>
  );
};

export default Combobox;
